import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Transient messages, the one thing the design system was knowingly short of
 * (PjCartSheet flagged the gap, PjItemSheet needed it for a missed required
 * option). In memory only: a toast a reload could resurrect is a toast that has
 * outlived the tap it was about.
 *
 * Calm by construction — the tones drive an icon, never a colour of alarm. An
 * unmet requirement in Pjazza reads as instruction, and a toast is no louder.
 */
export type PjToastTone = 'info' | 'warn' | 'success'

export interface PjToast {
  id: number
  message: string
  tone: PjToastTone
}

const DISMISS_AFTER_MS = 4000

/** More than a few stacked is a queue nobody reads; the oldest makes way. */
const MAX_VISIBLE = 3

export const useToastStore = defineStore('pjazza-toast', () => {
  const toasts = ref<PjToast[]>([])

  let sequence = 0
  const timers = new Map<number, ReturnType<typeof setTimeout>>()

  const clearTimer = (id: number): void => {
    const timer = timers.get(id)

    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }
  }

  const dismiss = (id: number): void => {
    toasts.value = toasts.value.filter(toast => toast.id !== id)
    clearTimer(id)
  }

  const arm = (id: number): void => {
    timers.set(
      id,
      setTimeout(() => dismiss(id), DISMISS_AFTER_MS)
    )
  }

  const push = (message: string, tone: PjToastTone = 'info'): void => {
    // A double-tap on the same blocked Add should re-say the one message, not
    // stack a second copy of it — refresh the timer on the existing one.
    const existing = toasts.value.find(toast => toast.message === message && toast.tone === tone)

    if (existing) {
      clearTimer(existing.id)
      arm(existing.id)

      return
    }

    const id = (sequence += 1)

    toasts.value = [...toasts.value, { id, message, tone }].slice(-MAX_VISIBLE)
    arm(id)
  }

  const info = (message: string): void => push(message, 'info')
  const warn = (message: string): void => push(message, 'warn')
  const success = (message: string): void => push(message, 'success')

  return { toasts, push, info, warn, success, dismiss }
})
